/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/**
 * The `flutter_inappwebview` JS bridge injected by the native MEX app. Handlers
 * registered on the native side are invoked via `callHandler(name)`. Same
 * contract as `callFlutterBridge` in mex-mobile-web.
 *
 * The bridge global is its own gate: it only exists inside the app webview, so
 * a desktop browser gets `undefined` back and callers fall through to their web
 * fallback.
 */
interface FlutterInAppWebView {
  callHandler?: (handler: string, ...args: unknown[]) => Promise<unknown> | void
}

/**
 * Asks the app to renew the session of the webview it already has open: it
 * re-runs GrabID and re-injects the auth cookies into this same webview, then
 * resolves. The app owns the session, so this is the only way to get a fresh
 * credential from inside the web page.
 *
 * It replaced a `reopen` handler that would have closed and reopened the
 * webview. Reopening was dropped before either side shipped it — refreshing in
 * place spares the merchant the close/open flicker — so nothing is on `reopen`
 * to stay compatible with.
 *
 * The app does NOT reload the page: the web side owns that, and must do it
 * once the call settles or the merchant sits on the error screen with a
 * perfectly good cookie underneath it.
 */
export const MEX_APP_REFRESH_AUTH_SESSION_HANDLER = "refreshAuthSession"

/** Only the `/shop` tab has a bottom nav bar; elsewhere the app ignores this. */
export const MEX_APP_BOTTOM_NAV_HANDLER = "setBottomNavVisibility"

/**
 * Arguments a navigation handler carries. The app's `NavigationHandler` reads
 * these by key — `orderId` for the tracker, `context` + `orderId` for chat,
 * `topic` for the Help Centre — and falls back to `args.deeplink` for anything
 * it does not map, so an unrecognised handler with no `deeplink` navigates
 * nowhere rather than failing loudly.
 *
 * Keys are the app's spelling (`orderId`, not the `orderID` used on the web
 * side).
 */
export type NativeHandlerArgs = Record<string, unknown>

/**
 * Invokes a native handler, optionally with a payload. Returns the native
 * promise, or `undefined` when the bridge is not present — callers must treat
 * `undefined` as "the app never heard this" and recover on the web side.
 *
 * `payload` is omitted from the call rather than passed as `undefined`, so a
 * no-argument handler on the Dart side still sees an empty argument list.
 */
const resolveBridge = (): FlutterInAppWebView | undefined =>
  ((window as unknown) as {
    flutter_inappwebview?: FlutterInAppWebView
  }).flutter_inappwebview

/**
 * Whether the app injected its JS bridge into this page at all.
 *
 * The gate a caller checks BEFORE it swaps a web control for a native hand-off:
 * `callFlutterBridge` reports the same thing, but only once the call has been
 * attempted, which is too late for UI that has to be built one way or the other.
 * An app build that never registered the specific handler still passes this — the
 * bridge answers per call, and nothing in the protocol lists what it knows.
 */
export const isNativeBridgeAvailable = (): boolean =>
  typeof resolveBridge()?.callHandler === "function"

export const callFlutterBridge = (
  handler: string,
  payload?: unknown,
): Promise<unknown> | undefined => {
  const bridge = resolveBridge()
  // `typeof`, not truthiness: a malformed injection (the key present but not a
  // function) would otherwise pass the guard, throw below, and be reported as a
  // rejected promise — which reads to a caller as "the app took the call and
  // failed it" rather than "there is no bridge here", and goes unhandled for
  // any caller that ignores the result.
  if (typeof bridge?.callHandler !== "function") return undefined
  try {
    return Promise.resolve(
      payload === undefined
        ? bridge.callHandler(handler)
        : bridge.callHandler(handler, payload),
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Shows or hides the app's bottom navigation bar around this webview.
 *
 * The app owns that bar for the whole session, so hiding it outlives the screen
 * that asked: whoever hides it is responsible for showing it again. Callers
 * should therefore assert a value rather than toggle one — see
 * `useAppBottomNavVisibility`, which sends it on every route change.
 *
 * Returns what {@link callFlutterBridge} returns, so the caller can tell a
 * missing bridge (`undefined`) from a call the app took and failed (a rejecting
 * promise) — the two want different handling, and neither is recoverable here.
 * A caller that ignores the result MUST still attach a `catch`, or a native
 * rejection surfaces as an unhandled rejection and reaches the error reporter
 * as a fault.
 */
export const setBottomNavVisibility = (
  visible: boolean,
): Promise<unknown> | undefined =>
  callFlutterBridge(MEX_APP_BOTTOM_NAV_HANDLER, { visible })

/**
 * What the app resolves `refreshAuthSession` with, per the agreed contract:
 * `{ success: true }`, or `{ success: false, error }` naming why the refresh
 * failed. Both keys are optional here because the contract is the app's to
 * keep, not ours to assume — see `refreshNativeAuthSession`.
 */
export interface RefreshAuthSessionResult {
  success?: boolean
  error?: string
}

/** Normalised outcome of a refresh attempt. */
export interface AuthSessionRefresh {
  /** False only when the app said so — `success: false`, or a bare `false`. */
  ok: boolean
  /**
   * Whether the app positively CONFIRMED the refresh (`success: true`, or a
   * bare `true`), as opposed to resolving something that merely is not a
   * failure. Kept apart from `ok` because the two answer different questions:
   * `ok` decides what to tell the merchant, `confirmed` is what tells a
   * working handler from one the installed build never registered — which
   * resolves `null` and would otherwise be indistinguishable from a real
   * success in the telemetry.
   */
  confirmed: boolean
  /** The app's own failure code, when it sent one. */
  error?: string
}

/**
 * Asks the app to renew the session in place, resolving to a normalised
 * outcome — or `null` when there is no bridge (desktop browser), which the
 * caller must recover from on the web side.
 *
 * Failure is only ever the app's own word for it: `success: false`, or a bare
 * `false`. The bare boolean is carved out deliberately — a handler answering
 * with a plain flag carries no `success` key to inspect, and unlike `null` it
 * is not the shape an unregistered handler produces, so reading it as success
 * would discard a clear negative.
 *
 * Everything else resolves `ok: true, confirmed: false`: `null` (what
 * `flutter_inappwebview` resolves for a handler the installed app build never
 * registered), an empty object, an undocumented shape. Two reasons for the
 * lenient default:
 *
 * - The app has form here: `pickImage` resolves a bare string where its spec
 *   says `{ data }`, so a strict reading of this shape would break on the same
 *   class of drift.
 * - The two mistakes are not equally bad. Reading a real success as a failure
 *   strands the merchant on the error screen with a working cookie underneath
 *   it; reading a real failure as a success costs one page load that re-raises
 *   the same screen. The caller reloads either way, so the lenient default is
 *   the recoverable one.
 *
 * Lenient for the merchant, precise for the report: `confirmed` keeps the
 * unconfirmed cases out of the success bucket in the telemetry.
 *
 * A rejection is left to propagate: it means the app took the call and threw,
 * which is a different thing from `success: false` and the caller reports it
 * separately.
 */
export const refreshNativeAuthSession = async (): Promise<AuthSessionRefresh | null> => {
  const pending = callFlutterBridge(MEX_APP_REFRESH_AUTH_SESSION_HANDLER)
  if (!pending) return null

  const answer = await pending
  if (typeof answer === "boolean") return { ok: answer, confirmed: true }

  const result = (answer ?? {}) as RefreshAuthSessionResult
  if (result.success === false) {
    return {
      ok: false,
      confirmed: true,
      ...(result.error ? { error: result.error } : {}),
    }
  }
  return { ok: true, confirmed: result.success === true }
}

/**
 * Image capture, handled natively because a web `<input type="file">` cannot
 * reach the app's camera or gallery. The app requests permission, picks,
 * compresses, and hands back base64.
 */
export const MEX_APP_PICK_IMAGE_HANDLER = "pickImage"

export interface PickImageOptions {
  /** Which picker to open. */
  source: "camera" | "gallery"
  /** Longest edge after resize. The app defaults to 1024 when omitted. */
  maxWidth?: number
  /** JPEG quality, 1-100. The app defaults to 80 when omitted. */
  quality?: number
}

/**
 * The app answers with exactly one of these keys. `canceled` (one `l`, the
 * app's spelling) and `permission_not_granted` are the two the merchant can
 * cause; anything else arrives as a raw message.
 */
export interface PickImageResult {
  data?: string
  error?: string
}

export const PICK_IMAGE_ERROR = {
  canceled: "canceled",
  permissionNotGranted: "permission_not_granted",
} as const

/**
 * Asks the app for one image, resolving to a base64 string.
 *
 * Resolves `null` in three cases the caller should treat identically — there is
 * nothing to add: the bridge is absent (desktop), the merchant cancelled, or
 * permission was refused. A genuine failure rejects, so a caller that wants to
 * surface an error can catch one without also catching a cancel.
 *
 * Optional fields are omitted rather than sent as `undefined` so the app falls
 * back to its own defaults instead of reading a null and overriding them.
 */
export const pickNativeImage = async (
  options: PickImageOptions,
): Promise<string | null> => {
  const pending = callFlutterBridge(MEX_APP_PICK_IMAGE_HANDLER, {
    source: options.source,
    ...(options.maxWidth === undefined ? {} : { maxWidth: options.maxWidth }),
    ...(options.quality === undefined ? {} : { quality: options.quality }),
  })
  if (!pending) return null

  const answer = await pending

  /*
   * Two shapes reach here. The spec documents `{ data }` / `{ error }`, but the
   * app's own handler resolves the encoded image on its own — treating a bare
   * string as `{}` would find no `.data` and report every pick as a cancel, so
   * the merchant would see the picker close and nothing appear.
   */
  if (typeof answer === "string") return answer || null

  const result = (answer ?? {}) as PickImageResult
  if (result.data) return result.data
  if (
    result.error === PICK_IMAGE_ERROR.canceled ||
    result.error === PICK_IMAGE_ERROR.permissionNotGranted
  ) {
    return null
  }
  if (result.error) throw new Error(result.error)
  return null
}
